import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import {
  captureUtmFromUrl,
  getStoredAttribution,
  clearStoredAttribution,
  getVisitorId,
  touchKey,
  isClaimed,
  markClaimed,
} from '@/lib/utm';

const TERMINAL_REASONS = ['already_attributed', 'expired_window', 'disabled'];

/**
 * Captura Parámetros UTM en cualquier página y, una vez que el usuario tiene
 * sesión (cualquiera de los 4 Flujos de Registro), reclama la atribución
 * server-side. Mismo esqueleto que useReferralAttribution.
 */
export function useUtmAttribution() {
  const location = useLocation();
  const { user } = useAuth();
  const sentTouches = useRef<Set<string>>(new Set());
  const claimedUsers = useRef<Set<string>>(new Set());

  // Efecto 1: registro del toque (best-effort, anónimo o autenticado)
  useEffect(() => {
    const touch = captureUtmFromUrl(location.search, location.pathname);
    if (!touch) return;
    const key = touchKey(touch.params, touch.landingPath);
    if (sentTouches.current.has(key)) return; // Requisito 3.4
    sentTouches.current.add(key);

    (async () => {
      try {
        await (supabase as any).rpc('register_utm_visit', {
          _visitor_id: getVisitorId(),
          _source: touch.params.source,
          _medium: touch.params.medium,
          _campaign: touch.params.campaign,
          _content: touch.params.content,
          _term: touch.params.term,
          _path: touch.landingPath,
        });
      } catch {
        /* la medición de visitas es best-effort: nunca interrumpe la navegación */
      }
    })();
  }, [location.search, location.pathname]);

  // Efecto 2: reclamo de la atribución en cuanto existe sesión (los 4 flujos)
  useEffect(() => {
    const uid = user?.id;
    if (!uid) return;
    if (claimedUsers.current.has(uid)) return; // Requisito 5.7
    if (isClaimed(uid)) return;
    claimedUsers.current.add(uid);

    (async () => {
      const stored = getStoredAttribution(); // null ⇒ reclamo de Fuente Directa
      try {
        const { data, error } = await (supabase as any).rpc('claim_utm_attribution', {
          _visitor_id: getVisitorId(),
          _source: stored?.params.source ?? null,
          _medium: stored?.params.medium ?? null,
          _campaign: stored?.params.campaign ?? null,
          _content: stored?.params.content ?? null,
          _term: stored?.params.term ?? null,
          _landing_path: stored?.landingPath ?? null,
          _first_seen_at: stored ? new Date(stored.ts).toISOString() : null,
        });
        if (error) {
          console.error('claim_utm_attribution error', error);
          return;
        }

        const reason = (data as any)?.reason;
        if ((data as any)?.ok || TERMINAL_REASONS.includes(reason)) {
          clearStoredAttribution(); // Requisito 5.5
          markClaimed(uid);
        }
      } catch (e) {
        console.error('claim_utm_attribution failed', e); // Requisito 5.6: se conserva
      }
    })();
  }, [user?.id]);
}
